import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../../Assets/logo.png";

import {
  fontFamilyAll,
  navLinkColor,
  primaryColor,
} from "../../Util/UtilColor";

import { navItems, bannerData, aboutData } from "../../Assets/data"; // <-- Import data

import Features from "../Features/Features";
import CargoDetails from "../CargoDetails/CargoDetails";
import BussinessPackage from "../BussinessPackage/BussinessPackage";
import FeaturesAndSkills from "../FeaturesAndSkills/FeaturesAndSkills";
import ContactUs from "../ContactUs/ContactUs";
import FooterCard from "../FooterCard/footerCard";
import BusinessToolCards from "../../Util/UtilCategoryCard/UtilCategoryCard";
import UtilHeader from "../../Util/UtilHeader/UtilHeader";
import ArticlePage from "../../Pages/articlePage/ArticlePage";
import SidebarWithContent from "../../Pages/documentPage/documentPage";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "../ScrollToTop";

const HomeContent = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const Styles = {
    navigationStyle: {
      fontFamily: fontFamilyAll,
      fontSize: { xs: "16px", sm: "18px", md: "20px" },
      fontWeight: "400",
      color: navLinkColor,
      textTransform: "none",
      mx: { xs: 1, sm: 2 },
    },
    aboutHeading: {
      fontSize: { xs: "28px", sm: "34px", md: "40px" },
      fontWeight: "900",
      fontFamily: fontFamilyAll,
      mb: { xs: 2, md: 3 },
    },
    aboutSubText: {
      fontSize: { xs: "16px", sm: "18px", md: "20px" },
      fontWeight: "lighter",
      fontFamily: fontFamilyAll,
      color: "grey",
      lineHeight: 1.6,
    },
    bannerStyle: {
      backgroundImage: `url(${bannerData.image})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      py: { xs: 10, sm: 14, md: 20 },
      px: { xs: 2, sm: 6, md: 12, lg: 30 },
      minHeight: { xs: "60vh", sm: "70vh", md: "80vh" },
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
    },
  };

  const location = useLocation();
  const navigate = useNavigate();

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (location.pathname !== "/" && id !== "knowledgebase") {
      navigate("/");
      setTimeout(() => {
        const targetElement = document.getElementById(id);
        if (targetElement) {
          const yOffset = -80;
          const y =
            targetElement.getBoundingClientRect().top +
            window.scrollY +
            yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    } else if (element) {
      const yOffset = -80;
      const y =
        element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.id}
            onClick={() => handleScroll(item.id)}
            sx={{ color: "black", textAlign: "center" }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <div>
      {/* Navbar */}
      <AppBar position="fixed" sx={{ p: { xs: 1, sm: 2 }, backgroundColor: primaryColor }}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box component="img" src={logo} alt="Eagle Logistics"
              sx={{ width: { xs: 120, sm: 150, md: 200 }, height: "auto" }} />
          </Box>

          {/* Desktop Nav */}
          <Box sx={{ display: { xs: "none", md: "flex" } }}>
            {navItems.map((item) => (
              <Button
                key={item.id}
                sx={Styles.navigationStyle}
                onClick={() => handleScroll(item.id)}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Mobile Menu */}
          <IconButton edge="end" onClick={handleDrawerToggle} sx={{ color: "white", display: { md: "none" } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
        {location.pathname !== "/" && <UtilHeader />}
      </AppBar>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: { xs: "100%", sm: "60%", md: "40%" } },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ mt: { xs: 8, sm: 10, md: 12 } }}>
        <ScrollToTop/>
        <Routes>
          <Route
            path="/"
            element={
              <Box sx={{ textAlign: "center", maxWidth: "1600px", mx: "auto" }}>
                {/* Banner */}
                <Box id="home" sx={Styles.bannerStyle}>
                  <Typography sx={{ fontFamily: fontFamilyAll, fontSize: { xs: 20, sm: 24, md: 30 }, fontWeight: 700, color: "white" }}>
                    {bannerData.headingTop}
                  </Typography>
                  <Typography sx={{ fontFamily: fontFamilyAll, fontSize: { xs: 28, sm: 40, md: 60, lg: 70 }, fontWeight: 900, color: "white" }}>
                    {bannerData.headingMain}
                  </Typography>
                  <Typography sx={{ fontFamily: fontFamilyAll, fontSize: { xs: 14, sm: 16, md: 18, lg: 20 }, fontWeight: 500, color: "white", maxWidth: { xs: "95%", sm: "80%", md: "600px" }, mx: "auto" }}>
                    {bannerData.subText}
                  </Typography>
                </Box>

                {/* Features */}
                <Box id="features">
                  <Features />
                </Box>

                {/* About Section */}
                <Box id="about" sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: "center", py: { xs: 2, sm: 4, md: 6 }, px: { xs: 2, sm: 4, md: 8 } }}>
                  <Box sx={{ width: { xs: "100%", md: "55%" }, mb: { xs: 2, md: 0 } }}>
                    <Box component="img" src={aboutData.image} alt="About Eagle" sx={{ width: "100%", height: "auto", borderRadius: 2, objectFit: "cover" }} />
                  </Box>
                  <Box sx={{ width: { xs: "100%", md: "45%" }, textAlign: "start", px: { xs: 2, sm: 3, md: 5 } }}>
                    <Typography sx={Styles.aboutHeading}>{aboutData.heading}</Typography>
                    <Typography sx={Styles.aboutSubText}>{aboutData.subText}</Typography>
                  </Box>
                </Box>

                {/* Packages */}
                <Box id="packages">
                  <CargoDetails />
                  <BussinessPackage />
                </Box>

                {/* Features & Skills */}
                <Box id="features-and-skills">
                  <FeaturesAndSkills />
                </Box>

                {/* Contact */}
                <Box id="contact">
                  <ContactUs />
                </Box>
              </Box>
            }
          />

          {/* Knowledgebase Routes */}
          <Route path="/knowledgebase" element={<BusinessToolCards />} />
          <Route path="/knowledgebase/article/:search/:id" element={<ArticlePage />} />
          <Route path="/knowledgebase/document/:recordid" element={<SidebarWithContent />} />
        </Routes>
      </Box>

      {/* Footer */}
      <FooterCard />
    </div>
  );
};

function Home() {
  return (
    <Router>
      <HomeContent />
    </Router>
  );
}

export default Home;
